<template>
    <Transition
        name="fade"
        mode="out-in"
    >
        <div
            v-if="activeForm === 'signup'"
            class="signup"
        >
            <template v-if="allowedToSignup">
                <div
                    v-if="progress && !isFreeAccountSignup"
                    class="signup__progress"
                >
                    {{ progress }}
                </div>

                <Transition
                    name="fade"
                    mode="out-in"
                >
                    <!-- Account details -->
                    <FormAccountDetails
                        v-if="currentStep === STEP_ACCOUNT_DETAILS"
                        :form-type="signupDefinition?.formType"
                        :authorization="signupDefinition.authorizationText"
                        :custom-field-enabled="signupDefinition.enableCustomField"
                        :custom-field-label="signupDefinition.customFieldLabel"
                        :custom-field-description="signupDefinition.customFieldDescription"
                        :campaign="campaignId"
                        :school="signupDefinition.externalIdType"
                        :student-number-enabled="signupDefinition.enableExternalId"
                        :student-number-label="signupDefinition.externalIdLabel"
                        :student-number-help-text="signupDefinition.externalIdHelpText"
                        :form-data="formData"
                        @submit="nextStep"
                        @login="showLogin"
                    />

                    <FormVerifyCode
                        v-else-if="currentStep === STEP_VERIFY"
                        :confirm-text="isFreeAccountSignup ? 'Bevestigen' : 'Volgende stap'"
                        @next-step="toCheckout"
                        @back-to-start="previousStep()"
                    >
                        <template #title>
                            {{ signupDefinition.stepTitle }}
                        </template>

                        <template #before>
                            <span v-html="signupDefinition.stepSubtitle" /><!-- eslint-disable-line -->
                        </template>
                    </FormVerifyCode>

                    <!-- User details -->
                    <FormUserDetails
                        v-else-if="currentStep === STEP_USER_DETAILS"
                        @submit="nextStep"
                    />

                    <FormPaymentInfo
                        v-else-if="currentStep === STEP_PAYMENT_INFO"
                        :membership="signupDefinition.membershipText"
                        :price="signupDefinition.priceText"
                        :start-date="signupDefinition.startDateText"
                        :name="`${formData.firstName} ${formData.lastName}`"
                        :address="`${formData.address}<br>${formData.zip} ${formData.city}`"
                        :is-submitting="isSubmitting"
                        @back-to-previous="previousStep()"
                        @submit="submitSignup"
                    >
                        <template #title>
                            {{ signupDefinition.stepTitle2 }}
                        </template>

                        <template #content>
                            <span v-html="signupDefinition.stepSubtitle2" /><!-- eslint-disable-line -->
                        </template>

                        <template
                            v-if="hasError"
                            #errors
                        >
                            Er ging helaas iets fout. Probeer het later nog eens, of stuur een mailtje naar
                            <a href="mailto:info@wearepublic.nl?subject=Er ging iets fout tijdens mijn aanmelding">
                                info@wearepublic.nl
                            </a>.
                        </template>
                    </FormPaymentInfo>

                    <!-- Success -->
                    <BasePanel
                        v-else-if="currentStep === STEP_SUCCESS
                            && !shouldAutomaticallyRedirectOnSuccess"
                    >
                        <template #title>{{ signupDefinition.stepTitle3 }}</template>
                        <!-- eslint-disable -->
                        <div class="panel__notice" v-html="signupDefinition.confirmationText"></div>
                        <!-- <p class="panel__notice" v-html=""></p> -->
                        <!-- eslint-enable -->

                        <template #after>
                            <NavLink
                                v-if="signupDefinition.callToAction?.element"
                                class="button dn-button button--secondary form__button--max-width"
                                :type="mapType(signupDefinition.callToAction.type)"
                                :url="mapURI(signupDefinition.callToAction)"
                                :blank="isExternal(signupDefinition.callToAction)"
                            >
                                {{ signupDefinition.callToAction.text }}
                            </NavLink>

                            <BaseButton
                                v-else
                                class="button--secondary button--stretch"
                                @click="greatSuccess"
                            >
                                Sluiten
                            </BaseButton>
                        </template>
                    </BasePanel>
                </Transition>
            </template>

            <template v-else>
                <BasePanel>
                    <template #title>
                        <template v-if="referralError">
                            Code ongeldig
                        </template>
                    </template>

                    <template #default>
                        <p v-if="referralError">
                            Helaas! Je kunt je niet aanmelden met deze code.
                            Wellicht kun je je vriend(in) vragen om nogmaals zijn of haar share link met je te delen.
                        </p>
                    </template>
                </BasePanel>
            </template>
        </div>

        <TheLogin
            v-else-if="activeForm === 'login'"
            :login-title="'Login met een bestaand account'"
            class="login"
        >
            <template #loginAfter>
                <BaseButton
                    class="button--stretch"
                    @click="showSignup"
                >
                    Terug
                </BaseButton>
            </template>
        </TheLogin>
    </Transition>
</template>

<script setup lang="ts">
import { useGtm } from '@gtm-support/vue-gtm';
import type { SignupFormFieldsFragment } from '~/types/craft-schema';
import { FORM_FREE } from '~/constants/signUpFormTypes';

const STEP_ACCOUNT_DETAILS = 'account-details',
    STEP_VERIFY = 'verify',
    STEP_USER_DETAILS = 'user-details',
    STEP_PAYMENT_INFO = 'payment-info',
    STEP_SUCCESS = 'success';

// eslint-disable-next-line no-shadow
enum ServerErrors {
    MEMBERSHIP_ALREADY_ACTIVATED = 'MEMBERSHIP_ALREADY_ACTIVATED',
}

const activeForm = ref<'signup' | 'login'>('signup');

function showLogin() {
    activeForm.value = 'login';
}

function showSignup() {
    activeForm.value = 'signup';
}

const { baseURL } = useRuntimeConfig().public;

const emit = defineEmits(['success', 'close']);

const props = defineProps<{
    signupDefinition: SignupFormFieldsFragment;
}>();

const { signupDefinition } = toRefs(props);

const userStore = useUserStore();

const { loggedIn, jwtToken } = storeToRefs(userStore);

const isFreeAccountSignup = computed(() => signupDefinition.value.formType === FORM_FREE);

const parsedSteps = computed(() => {
    if (isFreeAccountSignup.value && loggedIn.value) {
        return [STEP_SUCCESS];
    }

    if (isFreeAccountSignup.value) {
        return [STEP_ACCOUNT_DETAILS, STEP_VERIFY, STEP_SUCCESS];
    }

    if (loggedIn.value) {
        return [STEP_USER_DETAILS, STEP_PAYMENT_INFO, STEP_SUCCESS];
    }

    return [STEP_ACCOUNT_DETAILS, STEP_VERIFY, STEP_USER_DETAILS, STEP_PAYMENT_INFO, STEP_SUCCESS];
});

const { mapType, mapURI, isExternal } = useNavigation();

const {
    formData,

    currentStep,
    currentStepNum,
    totalSteps,

    nextStep,
    previousStep
} = useHasSteps(parsedSteps, parsedSteps.value[0]);

async function toCheckout(payload?: FormDataRecord) {
    nextStep(payload);

    if (isFreeAccountSignup.value) {
        await submitSignup({});
    }
}

const route = useRoute();

const isSubmitting = ref(false);
const hasError = ref(false);
const theError = ref<ServerErrors|null>(null);

const signUpStore = useSignUpStore();

const { token } = storeToRefs(signUpStore);

const referrerToken = computed(() => {
    if (!route.query || !route.query.sharedBy) {
        return;
    }

    return route.query.sharedBy;
});

const progress = computed(() => {
    // In this specific case, the last step does not count towards the total steps.
    if (currentStepNum.value < totalSteps.value) {
        return `Stap ${currentStepNum.value} van ${totalSteps.value - 1}`;
    }
    return null;
});


const referralError = computed(() => (signupDefinition.value.referralCodeRequired && !referrerToken.value));

const allowedToSignup = computed(() => {
    return (!referralError.value || (isFreeAccountSignup.value && !loggedIn.value));
});

const shouldAutomaticallyRedirectOnSuccess = computed(() => {
    if (
        !signupDefinition.value
        || !signupDefinition.value.successRedirect
        || signupDefinition.value.successRedirect.length === 0
    ) {
        return false;
    }

    return !!signupDefinition.value.successRedirect[0]?.uri;
});

const campaignId = computed(() => {
    if (
        signupDefinition.value.userCampaign.length > 0
        &&  signupDefinition.value.userCampaign[0].__typename === 'campaign_Category'
    ) {
        return signupDefinition.value.userCampaign[0]?.id;
    }

    return null;
});

const EXCEPTION_MANDRILL_UNKNOWN_TEMPLATE = 'Mandrill_Unknown_Template' as const;

type ErrorResponse = {
    error: ServerErrors;
    success: boolean;
};

const toEnum = <T,>(_enum: T, value: string) => {
    if (Object.values(_enum).includes(value)) {
        const errorString = value as keyof typeof _enum;
        return _enum[errorString];
    }

    return null;
};

const SIGN_UP_ENDPOINT = '/v2.0/membership/signup' as const;

const submitSignup = async(payload: FormDataRecord) => {
    isSubmitting.value = true;

    // Add last step to form data
    formData.value = { ...payload, ...formData.value };

    // Add campaign ID to form data, if available
    if (
        signupDefinition.value.userCampaign.length > 0
        && signupDefinition.value.userCampaign[0]
    ) {
        formData.value.campaign = campaignId.value;
    }

    if (signupDefinition.value.formType) {
        formData.value.freeAccountSignup = isFreeAccountSignup.value;
    }

    const { data, error } = await useFetchLaravel<{
        redirectUrl?: string;
        exception?: typeof EXCEPTION_MANDRILL_UNKNOWN_TEMPLATE;
    }>(SIGN_UP_ENDPOINT, {
        method: 'POST',
        data: {
            loggedIn: loggedIn.value,
            token: token.value,
            referrerToken: referrerToken.value,
            redirectBackUrl: shouldAutomaticallyRedirectOnSuccess.value
                ? `${baseURL}${route.path}`
                : null,
            ...formData.value
        }
    });

    if (data.value?.redirectUrl) {
        await navigateTo(data.value.redirectUrl, {
            external: true
        });
    } else if (!error.value) {
        nextStep();
    } else {
        const er = (error.value?.data as ErrorResponse)?.error;
        theError.value = toEnum(ServerErrors, er);
        hasError.value = true;
    }

    isSubmitting.value = false;
};

const { setCookies } = useJwtCookies();

const eventsStore = useEventsStore();

async function greatSuccess() {
    if (isFreeAccountSignup.value) {
        const { data } = await useFetchLaravel<{
            token: string;
        }>(
            '/v1.0/auth/jwt',
            {
                method: 'POST',
                data: {
                    email: formData.value.email,
                    password: formData.value.password,
                }
            }
        );

        if (data.value?.token) {
            jwtToken.value = data.value.token;

            await nextTick();

            setCookies(data.value.token);

            await nextTick();
    
            emit('success');

            await nextTick();

            await userStore.fetchUserRelations();

            emit('close');

            eventsStore.updateMyListHome();
        }
    }
}

watch(route, () => {
    if (route.query.state && route.query.state === 'succeeded') {
        currentStep.value = STEP_SUCCESS;
    }
}, {
    immediate: true
});

watch(currentStep, (value) => {
    // If the next step is the SUCCESS step, redirect to the succes page
    // BUT: this only happens when an automatic success page entry is
    // set in the CMS. Else the last 'success' step will be displayed
    // within the panel.
    if (value === STEP_SUCCESS && shouldAutomaticallyRedirectOnSuccess.value) {
        navigateTo({
            path: '/' + signupDefinition.value.successRedirect[0].uri
        });
    }

    // Send event to GTM to measure current signup step
    // Note: this doesn't track anything by default.
    // They should be handled within GTM or GA4
    if (value) {
        useGtm()?.push({
            event: 'signupStep',
            signupStep: value
        });
    }
}, {
    immediate: true
});
</script>

<style lang="less" src="./FormSignup.less" />
